import { hasIncompleteTourItem, hasInsuranceItem, isAddFlightsStepAvailable, isExperiencesSupportedToAddToCart, isFlightsPendingToAddToCart, isLuxPlusStandaloneStepAvailable, isNewFlightUpsellExperimentEnabled } from 'checkout/selectors/navigationSelectors'
import { CheckoutPageId } from 'checkout/constants/pages'
import { pushWithRegion, replaceWithRegion } from 'actions/NavigationActions'
import { last } from 'lib/array/arrayUtils'
import { getInflowFlightSearchParams } from 'checkout/selectors/flightSearchSelectors'
import { removeItem } from 'actions/CheckoutActions'
import config from 'constants/config'
import { getCharterFlightItems, getNonCharterFlightItems } from 'checkout/selectors/view/flights'
import { showCheckoutCartModeModal } from 'actions/UiActions'
import { getCheckoutPathWithCartId } from 'lib/url/pathsUtils'
import { navigateToNextStepTourCheckout } from 'components/App/Header/HeaderCheckout/StepsHeaderCheckout/steps/tours'
import { navigateToNextStepCruiseCheckout } from 'actions/CruiseActions'
import { getTourV2Items } from 'checkout/selectors/view/toursv2'
import type { AppAction } from 'actions/types/ActionTypes'
import { matchLocaleAwarePath } from 'lib/routing/matchLocaleAwarePath'
import { getCruiseItems } from 'checkout/selectors/cruiseSelectors'
import { getSubscriptionJoinItems, isLuxPlusSubscriptionInCart } from 'checkout/selectors/view/luxPlusSubscription'
import { buildFunnelCartAttributes } from 'checkout/selectors/funnel'
import { refreshFunnelOptimiserFlags } from 'actions/SystemActions'
import { findPostPurchaseCheckout } from 'lib/checkout/checkoutUtils'
import { fetchFlightJourneyV2 } from 'actions/FlightActions'
import { getCheckoutFlightItemFinalFare } from 'actions/CheckoutFlightActions'
import { getShouldShowFlightsBeforeLuxPlusUpsell, getIsFreePreviewLuxPlusMember } from 'luxPlus/selectors/featureToggle'

export function skipFlights(): AppAction {
  return async(dispatch, getState) => {
    const state = getState()
    const flightItems = getNonCharterFlightItems(state)
    const skippedFeederBundle = flightItems.some(item => !!item.bundleBudget)
    flightItems.forEach(item => {
      dispatch(removeItem(item.itemId))
    })

    if (skippedFeederBundle) {
      const updatedState = getState()
      const charterFlightItems = getCharterFlightItems(updatedState)
      charterFlightItems.forEach((item, index) => {
        if (item.searchId) {
          item.flights.forEach((flight) => {
            if (flight.journeyId) {
              dispatch(fetchFlightJourneyV2(item.searchId!, flight.journeyId, flight.fareFamily?.id))
            }
          })
        }

        dispatch(getCheckoutFlightItemFinalFare(item, index))
      })
    }

    await dispatch(navigateToNextPage())
  }
}

export function navigateToNextPage(replace?: boolean,
  navigateOptions?: {
    customFlightsSearchParams?: string
  },
): AppAction {
  return async(dispatch, getState) => {
    // Refresh funnel optimiser flags with current cart state before determining next page
    // This ensures step visibility decisions use the latest cart attributes
    const stateBeforeRefresh = getState()
    await dispatch(refreshFunnelOptimiserFlags(buildFunnelCartAttributes(stateBeforeRefresh)))

    const state = getState()
    const { customFlightsSearchParams } = navigateOptions ?? {}
    // let's work out what product flow on the checkout we are in
    const isTourV2 = getTourV2Items(state).length > 0
    const hasIncompleteTourV1Item = hasIncompleteTourItem(state)
    if (isTourV2 && !hasIncompleteTourV1Item) {
      // Must be a tour flow, use the tour step logic
      dispatch(navigateToNextStepTourCheckout(replace))
      return
    }

    const isCruise = getCruiseItems(state).length > 0
    if (isCruise) {
      // Must be a cruise flow, use the cruise step logic
      dispatch(navigateToNextStepCruiseCheckout(replace))
      return
    }

    // Must be a hotel flow, use the hotel step logic
    // Todo: Add support for other product flows
    const page = getNextCheckoutPage(state)
    const search = customFlightsSearchParams ?? getSearchParams(state, page)
    const cartId = state.checkout.cart.cartId

    const url = getCheckoutPathWithCartId(page, cartId)

    if (replace) {
      dispatch(replaceWithRegion(url, search))
    } else {
      dispatch(pushWithRegion(url, search))
    }
  }
}

interface NavigateToCheckoutOptions {
  replace?: boolean
  skipSpoofedModal?: boolean
  replaceCurrentWith?: string
}

export function navigateToCheckout({
  replace,
  skipSpoofedModal,
  replaceCurrentWith,
}: NavigateToCheckoutOptions = {}): AppAction {
  return async(dispatch, getState) => {
    const state = getState()

    // Replace current history entry first (if specified)
    // Can be used for removing query params before navigating to checkout
    if (replaceCurrentWith) {
      dispatch(replaceWithRegion(replaceCurrentWith))
    }

    if (
      config.UNIVERSAL_CHECKOUT_SPOOFED_MULTI_CART_ITEMS_ENABLED &&
      state.checkout.cart.isMultiItemMode &&
      !skipSpoofedModal
    ) {
      dispatch(showCheckoutCartModeModal())
    } else {
      dispatch(navigateToNextPage(replace))
    }
  }
}

export const restartCheckout = navigateToNextPage

function getHotelCheckoutStepTargetPage(state: App.State, stepId: CheckoutPageId): CheckoutPageId {
  if (
    stepId === CheckoutPageId.AddFlights &&
    getNonCharterFlightItems(state).length > 0 &&
    !isNewFlightUpsellExperimentEnabled(state)
  ) {
    return CheckoutPageId.DepartingFlights
  }

  return stepId
}

export function navigateToHotelCheckoutStep(stepId: CheckoutPageId, replace?: boolean): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    const targetStep = getHotelCheckoutStepTargetPage(state, stepId)
    const search = getSearchParams(state, targetStep)
    const cartId = state.checkout.cart.cartId
    const url = getCheckoutPathWithCartId(targetStep, cartId)

    if (replace) {
      dispatch(replaceWithRegion(url, search))
    } else {
      dispatch(pushWithRegion(url, search))
    }
  }
}

export function getCurrentCheckoutPage(state: App.State): string | undefined {
  const isCheckoutPage = matchLocaleAwarePath(state.router.location.pathname, { path: '/:regionCode/checkout', exact: false })
  return isCheckoutPage ? last(state.router.location.pathname.split('/')) : undefined
}

function getNextCheckoutPage(state: App.State): CheckoutPageId {
  const curPage = getCurrentCheckoutPage(state)
  const hasLuxPlusCheckoutItems = isLuxPlusSubscriptionInCart(state) || getSubscriptionJoinItems(state).length > 0
  const isFreePreviewLuxPlusMember = getIsFreePreviewLuxPlusMember(state)
  const shouldShowFlightsBeforeLuxPlusUpsell = getShouldShowFlightsBeforeLuxPlusUpsell(state)
  const shouldNavigateToLuxPlusStep = isLuxPlusStandaloneStepAvailable(state) ||
    (isFreePreviewLuxPlusMember && hasLuxPlusCheckoutItems)
  const isFlightStep = curPage === CheckoutPageId.AddFlights ||
    curPage === CheckoutPageId.DepartingFlights ||
    curPage === CheckoutPageId.ReturningFlights
  const shouldDeferLuxPlusUntilAfterFlights = shouldShowFlightsBeforeLuxPlusUpsell && (
    isAddFlightsStepAvailable(state) ||
    isFlightsPendingToAddToCart(state) ||
    curPage === CheckoutPageId.AddFlights ||
    curPage === CheckoutPageId.DepartingFlights ||
    curPage === CheckoutPageId.ReturningFlights
  )

  if (
    curPage !== CheckoutPageId.LuxPlus &&
    curPage !== CheckoutPageId.AddFlights &&
    curPage !== CheckoutPageId.DepartingFlights &&
    curPage !== CheckoutPageId.Experiences &&
    curPage !== CheckoutPageId.ReturningFlights &&
    shouldNavigateToLuxPlusStep &&
    !shouldDeferLuxPlusUntilAfterFlights
  ) {
    return CheckoutPageId.LuxPlus
  }

  if (
    curPage !== CheckoutPageId.AddFlights &&
    curPage !== CheckoutPageId.DepartingFlights &&
    curPage !== CheckoutPageId.ReturningFlights &&
    curPage !== CheckoutPageId.Experiences &&
    !(curPage === CheckoutPageId.LuxPlus && shouldShowFlightsBeforeLuxPlusUpsell) &&
    isAddFlightsStepAvailable(state) &&
    !isFlightsPendingToAddToCart(state)
  ) {
    return CheckoutPageId.AddFlights
  }

  if (
    curPage === CheckoutPageId.AddFlights &&
    getNonCharterFlightItems(state).length &&
    !isNewFlightUpsellExperimentEnabled(state)
  ) {
    return CheckoutPageId.DepartingFlights
  }

  if (
    curPage !== CheckoutPageId.AddFlights &&
    curPage !== CheckoutPageId.DepartingFlights &&
    curPage !== CheckoutPageId.ReturningFlights &&
    curPage !== CheckoutPageId.Experiences &&
    isAddFlightsStepAvailable(state) &&
    isFlightsPendingToAddToCart(state) &&
    isNewFlightUpsellExperimentEnabled(state)
  ) {
    return CheckoutPageId.AddFlights
  }

  if (
    curPage !== CheckoutPageId.DepartingFlights &&
    curPage !== CheckoutPageId.ReturningFlights &&
    curPage !== CheckoutPageId.Experiences &&
    isAddFlightsStepAvailable(state) &&
    isFlightsPendingToAddToCart(state)
  ) {
    return CheckoutPageId.DepartingFlights
  }

  if (
    isFlightStep &&
    shouldShowFlightsBeforeLuxPlusUpsell &&
    shouldNavigateToLuxPlusStep &&
    !isFlightsPendingToAddToCart(state)
  ) {
    return CheckoutPageId.LuxPlus
  }

  if (curPage !== CheckoutPageId.TourV1Capacity && hasIncompleteTourItem(state)) {
    return CheckoutPageId.TourV1Capacity
  }

  if (config.EXPERIENCES_ENABLED && curPage !== CheckoutPageId.Experiences && isExperiencesSupportedToAddToCart(state)) {
    return CheckoutPageId.Experiences
  }

  if (
    (curPage === CheckoutPageId.BedbankChangeDates ||
      curPage === CheckoutPageId.BedbankChangePackage ||
      curPage === CheckoutPageId.ChangeDates ||
      curPage === CheckoutPageId.ChangePackage) &&
      hasInsuranceItem(state)
  ) {
    return CheckoutPageId.InsuranceSelectDates
  }
  if (curPage === CheckoutPageId.AddExtraGuests && hasInsuranceItem(state)) {
    return CheckoutPageId.InsuranceAddExtraGuests
  }

  // Handle flight-change-dates mode: route to insurance step if order has insurance
  const postPurchaseMode = findPostPurchaseCheckout(state.checkout.cart.mode)
  if (
    postPurchaseMode === 'flight-change-dates' &&
    curPage !== CheckoutPageId.InsuranceSelectDates &&
    hasInsuranceItem(state)
  ) {
    return CheckoutPageId.InsuranceSelectDates
  }

  return CheckoutPageId.Purchase
}

function getSearchParams(state: App.State, page: CheckoutPageId): string | undefined {
  switch (page) {
    case CheckoutPageId.DepartingFlights:
      return getInflowFlightSearchParams(state)
  }
}
